// --------------------------------------------------------
// RegisterCompany - RegisterCompany
// --------------------------------------------------------

// --------------------------------------------------------
// IMPORTS
// --------------------------------------------------------
import { useCallback, useEffect, useState } from "react";
import ContainerWithSidebar from "../../../components/UI/containerWithSidebar/ContainerWithSidebar";
import SuccessModal from "../../../components/successModal/SuccessModal";
import { ReactComponent as MedicineCheckIcon } from "../../../assets/imgs/medicineCheck.svg";
import { useLocation, useNavigate } from "react-router-dom";
import { Paths } from "../../../enums/Paths";
import * as yup from "yup";
import * as companyActions from "../../../store/actions/company";
import { Formik } from "formik";
import {
  fillCompanyObjectForEdit,
  handleBackendErrors,
  validarCPF,
  validateCNPJ,
  validateEstado,
  validateOnlyLetters,
  validateTelephone,
} from "../../../helpers/HelperFunctions";
import RegisterCompanyForm from "../../../components/form/RegisterCompanyForm";
import { useAppDispatch } from "../../..";
import { initialObjectCompanyForm } from "../../../constants/Objects";
import { GenericSolidButton } from "../../../components/UI/buttons/Button.styled";
import GenericModal from "../../../components/genericModal/GenericModal";
import Colors from "../../../constants/Colors";
import useResponsiveInfo from "../../../hooks/useResponsiveInfo";
import { GeneralContainer } from "../../../components/UI/generalLayout/Layout.styled";
import { useSelector } from "react-redux";
import { CompanyService } from "../../../core/http/service/CompanyService";
import { ButtonMenu, Content, Menu, FooterButtonArea } from "./CompanyRegister.styled";
import CompanyIntegrationsForm from "../../../components/form/CompanyIntegrationsForm";
import BackButton from "../../../components/UI/buttons/BackButton";
import ScreenHeader from "../../../components/UI/screenHeader/ScreenHeader";
// --------------------------------------------------------
// RegisterCompany INTERFACE
// --------------------------------------------------------

/**
 * A component that renders a Screen.
 *
 * @param props Properties
 * @param props.route The route
 * @param props.navigation The navigator
 * @returns The component JSX.
 */
function RegisterCompany(): JSX.Element {
  const { state }: any = useLocation();
  const { isMobile } = useResponsiveInfo();
  const [err, setErrors] = useState<any[]>([]);
  const [companyUnits, setCompanyUnits] = useState<string[]>([]);
  const [abaActive, setAbaActive] = useState(1);

  const { company } = useSelector((state: any) => state);

  const getInitialValues = useCallback(() => {
    if (state != null) {
      const { companyState }: any = state;
      return fillCompanyObjectForEdit(companyState);
    } else {
      return initialObjectCompanyForm;
    }
  }, [state]);

  const fetchCompanyData = useCallback(async () => {
    if (state?.companyState) {
        const { companyState }: any = state;
        const service: CompanyService = new CompanyService();
        const result: any = await service.getCompanyById(companyState.id);
        setCompanyUnits(result.data.data.unidadesAtendimento);
    }
  }, [company, state]);

  useEffect(() => {
    fetchCompanyData();
  }, []);

  const [isConfirmationState, setIsConfirmationState] = useState(false);
  const [showModalCancellation, setShowModalCancellation] = useState(false);
  const [showModalConfirmation, setShowModalConfirmation] = useState(false);
  const [isEdit] = useState(state != null);
  const [isView] = useState(state != null && state.isView);
  const [initialValuesObject] = useState(getInitialValues());
  const { idsUnids } = useSelector((state: any) => state.company);

  const dispatch = useAppDispatch();

  const validationSchema = yup.object().shape({
    nomeFantasia: yup
      .string()
      .max(150)
      .min(3, "Preencha pelo menos 3 caracteres")
      .required("Nome Fantasia é um campo obrigatório")
      .test("validar-nome", "Digite apenas letras", value => {
        return validateOnlyLetters(value ?? "") === true;
      }),
    razaoSocial: yup
      .string()
      .min(3, "Preencha pelo menos 3 caracteres")
      .required("Nome da Empresa é um campo obrigatório")
      .test("validar-razao-social", "Digite apenas letras", value => {
        return validateOnlyLetters(value ?? "") === true;
      }),
    cnpj: yup
      .string()
      .required("CNPJ é um campo obrigatório")
      .test("validar-cnpj", "O CNPJ é inválido", value => {
        return validateCNPJ(value ?? "") === true;
      }),
    telefoneEmpresa: yup
      .string()
      .required("Telefone da empresa é um campo obrigatório"),
    cep: yup
      .string()
      .required("CEP é um campo obrigatório")
      .matches(new RegExp(/\d{2}[.]?\d{3}[-]?\d{3}/), "O CEP precisa ter 8 caracteres"),
    logradouro: yup
      .string()
      .max(200)
      .required("Endereço é um campo obrigatório"),
    numero: yup
      .string()
      .matches(new RegExp(/^[0-9]*$/, "g"), "Digite apenas números")
      .optional(),
    complemento: yup.string().max(50),
    cidade: yup.string().max(200).required("Cidade é um campo obrigatório"),
    bairro: yup.string().max(200).required("Bairro é um campo obrigatório"),
    estado: yup
      .string()
      .max(2)
      .required("Estado é um campo obrigatório")
      .test("validar-estado", "Sigla de Estado inexistente", value => {
        return validateEstado(value ?? "") === true;
      }), //Apenas siglas (pode ser feito também com test)
    nomeResponsavel: yup
      .string()
      .max(150)
      .min(10, "Preencha pelo menos 10 caracteres")
      .required("Nome do responsável é um campo obrigatório")
      .test("validar-nome-responsavel", "Digite apenas letras", value => {
        return validateOnlyLetters(value ?? "") === true;
      }),
    telefoneResponsavel: yup
      .string()
      .required("Telefone do responsável é um campo obrigatório")
      .test("validar-telefone", "Telefone inválido", value => {
        return validateTelephone(value ?? "") === true;
      }),
    telefoneResponsavel2: yup
      .string()
      .nullable()
      .optional()
      .test("validar-telefone", "Telefone inválido", value => {
        return value !== null && value !== ""
          ? validateTelephone(value ?? "") === true
          : true;
      }),
    cpfResponsavel: yup
      .string()
      .required("CPF do responsável é um campo obrigatório")
      .test("validar-cpf", "CPF inválido", value => {
        return validarCPF(value ?? "") === true;
      }), //usar test para validação módulo 11
    emailResponsavel: yup
      .string()
      .email("E-mail inválido!")
      .max(50)
      .required("E-mail do responsável é um campo obrigatório"),
    nomeAdministrador: yup
      .string()
      .max(150)
      .min(10, "Preencha pelo menos 10 caracteres")
      .required("Nome do administrador do sistema é um campo obrigatório")
      .test("validar-nome-responsavel", "Digite apenas letras", value => {
        return validateOnlyLetters(value ?? "") === true;
      }),
    cpfAdministrador: yup
      .string()
      .required("CPF do administrador do sistema é um campo obrigatório")
      .test("validar-cpf", "CPF inválido", value => {
        return validarCPF(value ?? "") === true;
      }), //usar test para validação módulo 11,
    emailAdministrador: yup
      .string()
      .email("E-mail inválido!")
      .max(50)
      .required("E-mail do administrador do sistema é um campo obrigatório"),
    telefoneAdministrador: yup
      .string()
      .required("Telefone do administrador do sistema é um campo obrigatório")
      .test("validar-telefone", "Telefone inválido", value => {
        return validateTelephone(value ?? "") === true;
      }),
    telefoneAdministrador2: yup
      .string()
      .nullable()
      .optional()
      .test("validar-telefone", "Telefone inválido", value => {
        return value !== null && value !== ""
          ? validateTelephone(value ?? "") === true
          : true;
      }),
  });

  const navigate = useNavigate();

  // --------------------------------------------------------
  // RegisterCompany PRIVATE DECLARATIONS
  // --------------------------------------------------------

  const navigateToCompanyList = () => {
    return navigate(Paths.CompanyList);
  };

  const validate = async (values: any) => {
    const isValid = await validationSchema.validate(values);
    if (isValid.valueOf()) {
      setIsConfirmationState(true);
    }
  };

  const registerCompany = async (values: any) => {
    try {
      await dispatch(companyActions.registerCompany(values, company.idsUnids));
      setShowModalConfirmation(true);
    } catch (error: any) {
      setShowModalConfirmation(false);

      handleBackendErrors(error, setErrors);
      //Montar um esquema de mensagens de erro aqui
      setIsConfirmationState(false);
    }
  };

  const updateCompany = async (values: any, companyId: string) => {
    try {
      await dispatch(companyActions.updateCompany(values, companyId, idsUnids));
      setShowModalConfirmation(true);
    } catch (error: any) {
      setShowModalConfirmation(false);
      handleBackendErrors(error, setErrors);
      //Montar um esquema de mensagens de erro aqui
      setIsConfirmationState(false);
    }
  };

  const onConfirmarCadastro = async (values: any) => {
    isConfirmationState
      ? isEdit
        ? await updateCompany(values, initialValuesObject.id)
        : await registerCompany(values)
      : await validate(values);
  };

  const onVoltar = () => {
    return navigate(Paths.CompanyList);
  };

  const onVoltarConfirmacaoCadastro = () => {
    if (isConfirmationState) {
      setIsConfirmationState(false);
    }
  };

  const onCancelModalCancellation = () => {
    setShowModalCancellation(false);
  };

  const onConfirmModalCancellation = () => {
    setShowModalCancellation(false);
    return navigateToCompanyList();
  };
  const openModalCancellation = (values: Object) => {
    const valueList = Object.values(values).filter(value => {
      return value !== "";
    });

    if (valueList.length > 0) {
      setShowModalCancellation(true);
    } else {
      navigateToCompanyList();
    }
  };

  const getScreenTitle = () => {
    if (isConfirmationState) return "Confirmar Dados da Empresa";
    if (isView) return "Visualizar Empresa";
    if (isEdit) return "Editar Empresa";
    return "Cadastrar Empresa";
  };

  return (
    <ContainerWithSidebar>
      <GeneralContainer>
        <BackButton text="Voltar para listagem de empresas" onClick={onVoltar} />
        <ScreenHeader
          hasRightButton={false}
          screenTitle={getScreenTitle()}
          rightButtonText=""
          onRightButtonClick={() => {}}
          errors={err}
          onCloseError={() => setErrors([])}
        />
        <Formik
          initialValues={initialValuesObject}
          onSubmit={() => {}}
          validationSchema={validationSchema}
          validateOnChange={false}
          validateOnBlur={false}
        >
          {formik => {
            const { values, errors, touched } = formik;
            return (
              <>
                <Menu isMobile={isMobile}>
                  <ButtonMenu
                    onClick={() => setAbaActive(1)}
                    selected={abaActive === 1}
                  >
                    Dados da Empresa
                  </ButtonMenu>
                  <ButtonMenu
                    onClick={() => setAbaActive(2)}
                    selected={abaActive === 2}
                  >
                    Integrações
                  </ButtonMenu>
                </Menu>
                <Content>
                  {abaActive === 1 ? (
                    <RegisterCompanyForm
                      unidadesAtendimento={companyUnits}
                      errors={err}
                      onCloseError={() => {
                        setErrors([]);
                      }}
                      initialValues={initialValuesObject}
                      isEditState={isEdit}
                      isViewState={isView}
                      isConfirmationState={isConfirmationState}
                      formValues={values}
                      formErrors={errors}
                      formTouched={touched}
                      onClickCancel={
                        isConfirmationState
                          ? onVoltarConfirmacaoCadastro
                          : () => {
                              openModalCancellation(values);
                            }
                      }
                      onClickConfirmar={async () => {
                        await onConfirmarCadastro(values);
                      }}
                    />
                  ) : (
                    <CompanyIntegrationsForm
                      isEditState={isEdit}
                      isViewState={isView}
                      initialValues={initialValuesObject}
                    />
                  )}
                </Content>
                {abaActive === 2 && !isView && (
                    <FooterButtonArea>
                        <GenericSolidButton
                            color={Colors.SPM600}
                            onClick={async () => {
                                await onConfirmarCadastro(values);
                            }}
                        >
                            Salvar Integrações
                        </GenericSolidButton>
                    </FooterButtonArea>
                )}
              </>
            );
          }}
        </Formik>
        {isView && (
          <>
            <GenericSolidButton
              onClick={onVoltar}
              color={Colors.SPM600}
              style={{
                width: isMobile ? "100%" : "10%",
                marginTop: 24,
                marginBottom: isMobile ? 24 : 2,
              }}
            >
              Voltar
            </GenericSolidButton>
          </>
        )}

        <GenericModal
          modalTitle={"Tem certeza que deseja sair do cadastro de empresa?"}
          modalSubtitle={"Os dados preenchidos não serão salvos"}
          cancelButtonMsg={"Confirmar"}
          confirmButtonMsg={"Voltar"}
          visible={showModalCancellation}
          onCancel={onCancelModalCancellation}
          onConfirm={onConfirmModalCancellation}
        />

        <SuccessModal
          Icon={() => {
            return <MedicineCheckIcon />;
          }}
          modalMsg={
            isEdit
              ? "Empresa atualizada com sucesso!"
              : "Cadastro concluído com sucesso!"
          }
          visible={showModalConfirmation}
          onVoltar={navigateToCompanyList}
        />
      </GeneralContainer>
    </ContainerWithSidebar>
  );
}

export default RegisterCompany;
